import React, { Component } from "react";
import { Table, Row, Col, Button, Input, Form, FormGroup } from "reactstrap";
import { toast } from "react-toastify";
import http from "../services/httpService";
import DefaultPanelContainer from "./common/defaultPanelContainer";
import { AdminPermission } from "./adminPermission";

class BlockedWebsitesTable extends Component {
  state = { 
    data: [], 
    loading: true, 
    newWebsite: "",
    adding: false,
    sources: []
  };

  async componentDidMount() {
    await this.loadSources();
    await this.loadData();
  }

  loadData = async () => {
    try {
      const { data } = await http.get(`userBlockedWebsites/${this.props.userId}`);
      this.setState({ data, loading: false });
    } catch (error) {
      console.error("Error loading blocked websites:", error);
      toast.error("Failed to load blocked websites");
      this.setState({ loading: false });
    }
  };

  loadSources = async () => {
    try {
      const { data } = await http.get("sources");
      const domains = (data || [])
        .map(s => (s && s.domain ? s.domain.toLowerCase().trim() : null))
        .filter(Boolean);
      this.setState({ sources: domains });
    } catch (error) {
      console.error("Error loading sources:", error);
      toast.error("Failed to load sources for suggestions");
    }
  };

  handleNewWebsiteChange = (e) => {
    this.setState({ newWebsite: e.target.value });
  };

  handleAddWebsite = async (e) => {
    e.preventDefault();
    const { newWebsite, sources } = this.state;
    
    if (!newWebsite.trim()) {
      toast.error("Please enter a website");
      return;
    }

    const normalized = newWebsite.trim().toLowerCase();
    if (!sources.includes(normalized)) {
      toast.error("Website must be one of the known sources");
      return;
    }

    this.setState({ adding: true });

    try {
      await http.post("userBlockedWebsites", {
        userId: this.props.userId,
        website: normalized
      });
      
      toast.success("Website blocked successfully");
      this.setState({ newWebsite: "" });
      await this.loadData();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        toast.error(error.response.data);
      } else {
        toast.error("Failed to block website");
      }
    } finally {
      this.setState({ adding: false });
    }
  };

  handleDeleteWebsite = async (id, website) => {
    if (!window.confirm(`Are you sure you want to unblock ${website}?`)) {
      return;
    }

    try {
      // API expects: DELETE /api/userBlockedWebsites/{userId}/{website}
      await http.delete(`userBlockedWebsites/${this.props.userId}/${encodeURIComponent(website)}`);
      toast.success("Website unblocked successfully");
      await this.loadData();
    } catch (error) {
      toast.error("Failed to unblock website");
    }
  };

  renderAddWebsiteForm = () => {
    const { newWebsite, adding, sources } = this.state;

    return (
      <AdminPermission>
        <Form onSubmit={this.handleAddWebsite} className="mb-3">
          <Row>
            <Col md={8}>
              <FormGroup>
                <Input
                  type="text"
                  placeholder="Select website to block"
                  value={newWebsite}
                  onChange={this.handleNewWebsiteChange}
                  disabled={adding}
                  list="source-domains"
                />
                <datalist id="source-domains">
                  {sources.map((d) => (
                    <option value={d} key={d} />
                  ))}
                </datalist>
              </FormGroup>
            </Col>
            <Col md={4}>
              <Button 
                type="submit" 
                color="primary" 
                disabled={adding || !newWebsite.trim()}
              >
                {adding ? "Adding..." : "Block Website"}
              </Button>
            </Col>
          </Row>
        </Form>
      </AdminPermission>
    );
  };

  renderEmptyState = () => {
    return (
      <DefaultPanelContainer title="Blocked Websites">
        <p>No websites are currently blocked for this user.</p>
        {this.renderAddWebsiteForm()}
      </DefaultPanelContainer>
    );
  };

  render() {
    const { data, loading } = this.state;
    
    if (loading) return <p>Loading blocked websites...</p>;
    
    if (!data || data.length === 0) {
      return this.renderEmptyState();
    }

    return (
      <DefaultPanelContainer title="Blocked Websites">
        {this.renderAddWebsiteForm()}
        <Table>
          <thead>
            <tr>
              <th>Website</th>
              <th>Blocked Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((website) => (
              <tr key={website.id}>
                <td>{website.website}</td>
                <td>{new Date(website.createdDate).toLocaleString()}</td>
                <td>
                  <AdminPermission>
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => this.handleDeleteWebsite(website.id, website.website)}
                    >
                      Unblock
                    </Button>
                  </AdminPermission>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </DefaultPanelContainer>
    );
  }
}

export default BlockedWebsitesTable;
